import { useState } from 'react';
import { authFetch, getSession } from '../api';
import CopyBlock from '../components/CopyBlock';

const EXAMPLES = [
  {
    icon: '🧠',
    title: 'AI Data Analyst',
    desc: 'Train an AI on your dataset and let others query it via natural language. Example: a market analytics bot that runs SQL and Python against 15GB of OHLCV data.',
    tags: 'ai, analytics, data',
  },
  {
    icon: '🔌',
    title: 'MCP Tool Server',
    desc: 'Wrap your MCP tools as an API. Claude or any AI agent can discover and call your tools through our proxy — with billing built in.',
    tags: 'mcp, tools, agents',
  },
  {
    icon: '📊',
    title: 'Specialized Database',
    desc: 'Expose a curated database — research papers, legal docs, real estate listings, sports stats — as a queryable endpoint. Charge per query.',
    tags: 'database, search, query',
  },
  {
    icon: '🤖',
    title: 'Fine-tuned Model',
    desc: 'Run a fine-tuned or local model (Ollama, vLLM, etc.) and charge per call. Specialized models for code review, translation, medical Q&A, and more.',
    tags: 'llm, model, inference',
  },
  {
    icon: '🔍',
    title: 'Scraper / Monitor',
    desc: 'A service that collects real-time data — prices, news, social sentiment — and serves it on demand. Your machine does the work, consumers pay per request.',
    tags: 'scraping, monitoring, data',
  },
  {
    icon: '⚙️',
    title: 'Computation Service',
    desc: 'GPU-heavy tasks: image generation, video processing, backtesting, simulations. Your hardware, their requests, automatic billing.',
    tags: 'gpu, compute, processing',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'Build your service',
    desc: 'Create an HTTP server that accepts POST requests with JSON bodies and returns JSON responses. It can run anywhere — your laptop, a VPS, a Raspberry Pi.',
  },
  {
    num: '2',
    title: 'Add a /health endpoint',
    desc: 'Your server must respond to GET /health with HTTP 200. Our platform pings this every 60 seconds — if it responds, your build shows as live.',
  },
  {
    num: '3',
    title: 'Expose it publicly',
    desc: 'Use a tunnel service to make your local server accessible from the internet. Free options: Cloudflare Tunnel (permanent URL) or ngrok (temporary URL).',
  },
  {
    num: '4',
    title: 'Register it here',
    desc: 'Fill in the form below with your endpoint URL, set your price per call, and publish. We handle billing, API keys, rate limiting, and the marketplace listing.',
  },
  {
    num: '5',
    title: 'Add tools (optional)',
    desc: 'Define specific tools/commands in your Dashboard — each with its own price and description. Consumers see these as your build\'s library.',
  },
];

// ── MCP setup tabs (Claude Desktop / Claude Code / Other) ────────────────────
function McpSetupTabs() {
  const [tab, setTab] = useState('desktop');

  const tabStyle = (active) => ({
    padding: '8px 18px', borderRadius: 'var(--radius-sm)',
    border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? '#fff' : 'var(--muted)',
    fontWeight: 600, fontSize: 13, cursor: 'pointer',
  });

  const desktopConfig = `{
  "mcpServers": {
    "invokeAPI": {
      "url": "https://www.invoke-api.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`;

  const codeConfig = `claude mcp add invokeapi \\
  --transport streamable-http \\
  "https://www.invoke-api.com/mcp" \\
  --header "Authorization: Bearer YOUR_API_KEY"`;

  const cursorConfig = `{
  "mcpServers": {
    "invokeAPI": {
      "url": "https://www.invoke-api.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`;

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Step 1: Get your API key</div>
      <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
        Sign up at <span style={{ color: 'var(--accent2)', fontFamily: 'monospace' }}>invoke-api.com</span> and copy your <span style={{ color: 'var(--accent2)', fontFamily: 'monospace' }}>am_live_xxx</span> key from the dashboard.
        This key works for everything — building, consuming, and managing builds.
      </p>

      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Step 2: Add MCP config</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button style={tabStyle(tab === 'desktop')} onClick={() => setTab('desktop')}>Claude Desktop</button>
        <button style={tabStyle(tab === 'code')} onClick={() => setTab('code')}>Claude Code</button>
        <button style={tabStyle(tab === 'other')} onClick={() => setTab('other')}>Cursor / Other</button>
      </div>

      {tab === 'desktop' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Open Settings → Developer → Edit Config, then paste this into <code style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>claude_desktop_config.json</code>:
          </p>
          <CopyBlock text={desktopConfig} label="Copy config" />
        </div>
      )}

      {tab === 'code' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Run this in your terminal:
          </p>
          <CopyBlock text={codeConfig} label="Copy command" />
        </div>
      )}

      {tab === 'other' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            For Cursor, Windsurf, or any MCP-compatible client, add this to your MCP settings file:
          </p>
          <CopyBlock text={cursorConfig} label="Copy config" />
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 6 }}>Step 3: Start chatting</div>
      <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
        Restart Claude (or your client), and you're connected. You can now create builds, add tools, manage listings, <em>and</em> call other people's builds — all from chat.
      </p>
    </div>
  );
}

export default function RegisterBuild({ navigate }) {
  const session = getSession();
  const [showForm, setShowForm] = useState(false);
  const [showGuide, setShowGuide] = useState(!session);
  const [form, setForm] = useState({
    name: '', slug: '', description: '',
    endpointUrl: '', pricePerCall: '0.10', tags: '',
    exampleQueries: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function autoSlug(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  async function submit() {
    setError('');
    if (!form.name || !form.endpointUrl) {
      setError('Build name and endpoint URL are required.');
      return;
    }
    setLoading(true);
    try {
      const r = await authFetch('/v1/builds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          slug: form.slug || autoSlug(form.name),
          description: form.description,
          endpointUrl: form.endpointUrl,
          pricePerCall: parseFloat(form.pricePerCall) || 0.10,
          tags: form.tags,
          exampleQueries: form.exampleQueries.split('\n').map(s => s.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (d.error) { setError(d.error); setLoading(false); return; }
      setResult({ ...d, slug: form.slug || autoSlug(form.name) });
    } catch {
      setError('Request failed. Is the server running?');
    }
    setLoading(false);
  }

  const s = {
    h1: { fontSize: 28, fontWeight: 700, marginBottom: 8 },
    sub: { fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 32 },
    h2: { fontSize: 20, fontWeight: 600, marginTop: 40, marginBottom: 16 },
    form: { maxWidth: 600 },
    section: { marginBottom: 28 },
    sectionTitle: { fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 },
    row: { marginBottom: 16 },
    label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 },
    input: {
      width: '100%', padding: '10px 12px',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)',
      background: 'var(--bg2)',
      color: 'var(--text)', fontSize: 14, outline: 'none',
      boxSizing: 'border-box',
    },
    hint: { fontSize: 12, color: 'var(--muted)', marginTop: 4 },
    btn: {
      padding: '11px 28px', borderRadius: 'var(--radius-sm)',
      border: 'none', background: 'var(--accent)',
      color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer',
    },
    btnOutline: {
      padding: '10px 22px', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)', background: 'transparent',
      color: 'var(--text)', fontWeight: 500, fontSize: 14, cursor: 'pointer',
    },
    error: { color: 'var(--red)', fontSize: 13, marginBottom: 16 },
    success: {
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 28, maxWidth: 560,
    },
    successTitle: { fontSize: 20, fontWeight: 600, marginBottom: 6 },
    successSub: { color: 'var(--muted)', marginBottom: 24 },
    keyBox: {
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: 14,
      fontFamily: 'monospace', fontSize: 13, color: 'var(--accent2)',
      wordBreak: 'break-all', marginBottom: 8,
    },
    keyLabel: { fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 },
    goBtn: {
      padding: '10px 22px', borderRadius: 'var(--radius-sm)',
      border: 'none', background: 'var(--accent)',
      color: '#fff', fontWeight: 600, fontSize: 14, marginTop: 20, cursor: 'pointer',
    },
    signInBox: { textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' },
    exCard: {
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 20,
    },
    exIcon: { fontSize: 28, marginBottom: 10 },
    exTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 },
    exDesc: { fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 8 },
    exTags: { fontSize: 11, color: 'var(--accent2)', fontFamily: 'monospace' },
    stepRow: { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 },
    stepNum: {
      flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
      background: 'var(--accent)', color: '#fff', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700,
    },
    stepTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 },
    stepDesc: { fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 },
    howItWorks: {
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 24, marginTop: 32, marginBottom: 32,
    },
    diagram: {
      fontFamily: 'monospace', fontSize: 13, color: 'var(--accent2)',
      lineHeight: 1.8, padding: '16px 0',
    },
  };

  // ── Not signed in ─────────────────────────────────────────────────────
  if (!session) return (
    <div>
      <h1 style={s.h1}>Publish a build</h1>
      <p style={s.sub}>
        Turn any server, AI model, database, or script into a monetized API endpoint.
        You keep your data on your machine — consumers pay per call through our proxy.
      </p>

      {/* What is a Build */}
      <h2 style={s.h2}>What can you publish?</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, marginBottom: 32 }}>
        {EXAMPLES.map((ex, i) => (
          <div key={i} style={s.exCard}>
            <div style={s.exIcon}>{ex.icon}</div>
            <div style={s.exTitle}>{ex.title}</div>
            <div style={s.exDesc}>{ex.desc}</div>
            <div style={s.exTags}>{ex.tags}</div>
          </div>
        ))}
      </div>

      {/* Deploy with Claude — visible even when not signed in */}
      <div style={{ background: 'linear-gradient(135deg, var(--bg2) 0%, var(--bg) 100%)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 28, marginBottom: 32 }}>
        <h2 style={{ ...s.h2, marginTop: 0, color: 'var(--accent)' }}>Fastest way: Deploy with Claude</h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 16 }}>
          Connect invokeAPI to <strong style={{ color: 'var(--text)' }}>Claude Desktop</strong>, <strong style={{ color: 'var(--text)' }}>Claude Code</strong>, or any MCP client.
          Then just ask Claude to create and manage your build — all through chat.
          Same connection works for calling other builds too. <strong style={{ color: 'var(--text)' }}>One setup does everything.</strong>
        </p>
        <McpSetupTabs />
      </div>

      <div style={{ textAlign: 'center', padding: 20 }}>
        <p style={{ color: 'var(--muted)', marginBottom: 20 }}>Sign in to publish via the web form, or use the MCP setup above to deploy from Claude.</p>
        <button style={s.btn} onClick={() => navigate('signin')}>Sign in</button>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 16 }}>
          No account? <span style={{ color: 'var(--accent2)', cursor: 'pointer' }} onClick={() => navigate('getkey')}>Create one free</span>
        </p>
      </div>
    </div>
  );

  // ── Success ───────────────────────────────────────────────────────────
  if (result) return (
    <div>
      <h1 style={s.h1}>Build published!</h1>
      <p style={{ ...s.sub, marginBottom: 28 }}>Save your credentials below — the webhook secret is shown only once.</p>
      <div style={s.success}>
        <div style={s.successTitle}>{form.name}</div>
        <div style={s.successSub}>Slug: {result.slug}</div>

        <div style={s.keyLabel}>Build ID</div>
        <div style={s.keyBox}>{result.buildId}</div>

        <div style={s.keyLabel}>Webhook secret (sign incoming requests)</div>
        <div style={s.keyBox}>{result.webhookSecret}</div>

        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 16 }}>
          Your build is registered but <strong style={{ color: '#eab308' }}>offline</strong> until your endpoint passes the health check.
          Point <strong style={{ color: 'var(--text)' }}>{form.endpointUrl}/health</strong> to return HTTP 200 and it will go live within 60 seconds.
        </div>

        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>
          Next: Go to your <strong style={{ color: 'var(--accent2)', cursor: 'pointer' }} onClick={() => navigate('dashboard')}>Dashboard</strong> to add tools, run a security scan, and toggle your build live.
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button style={s.goBtn} onClick={() => navigate('dashboard')}>Go to Dashboard</button>
          <button style={{ ...s.goBtn, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }} onClick={() => navigate('marketplace')}>View Marketplace</button>
        </div>
      </div>
    </div>
  );

  // ── Main page ─────────────────────────────────────────────────────────
  return (
    <div>
      <h1 style={s.h1}>Publish a build</h1>
      <p style={s.sub}>
        A <strong style={{ color: 'var(--text)' }}>build</strong> is any service you run that accepts API calls.
        It can be an AI model, a database, a scraper, a computation pipeline — anything that responds to HTTP requests.
        You host it. We handle billing, API keys, rate limiting, and marketplace discovery.
      </p>

      {/* What is a Build — examples */}
      {!showForm && (
        <>
          {/* Collapsible guide for signed-in users */}
          {session && !showGuide && (
            <div style={{ marginBottom: 20 }}>
              <span
                style={{ fontSize: 14, color: 'var(--accent2)', cursor: 'pointer', fontWeight: 500 }}
                onClick={() => setShowGuide(true)}
              >
                Show setup guide {'\u25B6'}
              </span>
            </div>
          )}
          {session && showGuide && (
            <div style={{ marginBottom: 8 }}>
              <span
                style={{ fontSize: 14, color: 'var(--accent2)', cursor: 'pointer', fontWeight: 500 }}
                onClick={() => setShowGuide(false)}
              >
                Hide setup guide {'\u25BC'}
              </span>
            </div>
          )}

          {showGuide && (
            <>
              <h2 style={s.h2}>What can you publish?</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, marginBottom: 32 }}>
                {EXAMPLES.map((ex, i) => (
                  <div key={i} style={s.exCard}>
                    <div style={s.exIcon}>{ex.icon}</div>
                    <div style={s.exTitle}>{ex.title}</div>
                    <div style={s.exDesc}>{ex.desc}</div>
                    <div style={s.exTags}>{ex.tags}</div>
                  </div>
                ))}
              </div>

              {/* How it works */}
              <div style={s.howItWorks}>
                <h2 style={{ ...s.h2, marginTop: 0 }}>How it works</h2>
                <div style={s.diagram}>
                  Consumer sends request  →  invokeAPI proxy  →  Your endpoint{'\n'}
                  {'                              '}↓{'\n'}
                  {'                    '}Deducts credits, logs call{'\n'}
                  {'                    '}You get 70%, platform gets 30%
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
                  Your data never leaves your machine. The proxy forwards consumer requests to your endpoint
                  and returns your response. We handle authentication, billing, and abuse protection.
                </div>
              </div>

              {/* Steps */}
              <h2 style={s.h2}>How to set up</h2>
              {STEPS.map((step, i) => (
                <div key={i} style={s.stepRow}>
                  <div style={s.stepNum}>{step.num}</div>
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    <div style={s.stepTitle}>{step.title}</div>
                    <div style={s.stepDesc}>{step.desc}</div>
                  </div>
                </div>
              ))}

              {/* Endpoint requirements */}
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, marginTop: 20, marginBottom: 32 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Endpoint requirements</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
                  <div>1. <strong style={{ color: 'var(--text)' }}>GET /health</strong> — Must return HTTP 200 (this is how we know you're online)</div>
                  <div>2. <strong style={{ color: 'var(--text)' }}>POST /</strong> — Accept JSON body, return JSON response (this is what consumers call)</div>
                  <div>3. <strong style={{ color: 'var(--text)' }}>HTTPS</strong> — Use Cloudflare Tunnel or ngrok to get a public HTTPS URL</div>
                  <div>4. <strong style={{ color: 'var(--text)' }}>No auth required</strong> — Our proxy handles consumer authentication. Your endpoint just processes requests.</div>
                </div>
              </div>

              {/* Minimal server example */}
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Minimal endpoint (Python + FastAPI)</div>
                <pre style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', padding: 16, overflowX: 'auto',
                  fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6, color: 'var(--accent2)',
                }}>{`from fastapi import FastAPI
import uvicorn

app = FastAPI()

@app.get("/health")
def health():
    return {"status": "ok"}

@app.post("/")
def handle(body: dict):
    # Your logic here — query a DB, run AI, etc.
    return {"result": "hello from my build!"}

uvicorn.run(app, host="0.0.0.0", port=8000)`}</pre>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                  Then expose it: <code style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>cloudflared tunnel --url http://localhost:8000</code> or <code style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>ngrok http 8000</code>
                </div>
              </div>
            </>
          )}

          {/* ── Deploy with Claude (MCP) ────────────────────────────────── */}
          <div style={{ background: 'linear-gradient(135deg, var(--bg2) 0%, var(--bg) 100%)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 28, marginBottom: 32 }}>
            <h2 style={{ ...s.h2, marginTop: 0, color: 'var(--accent)' }}>Easiest way: Deploy with Claude</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 20 }}>
              Add invokeAPI as an MCP server in <strong style={{ color: 'var(--text)' }}>Claude Desktop</strong> or <strong style={{ color: 'var(--text)' }}>Claude Code</strong>, then just <em>ask Claude</em> to create, manage, and publish your build — all through chat.
              The same connection lets you call other people's builds too. <strong style={{ color: 'var(--text)' }}>One setup, you're both a builder and a consumer.</strong>
            </p>

            <McpSetupTabs />

            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text)' }}>Then just tell Claude:</div>
              <CopyBlock id="claude-prompt" label="Copy prompt" text={`I have invokeAPI connected as an MCP server. Here's what I need:

1. Create a build called "[YOUR BUILD NAME]" with endpoint URL "[YOUR HTTPS URL]"
   - Description: [what it does]
   - Tags: [relevant keywords]
   - Price per call: $0.10

2. Add these tools to it:
   - [tool name]: [what it does] — example payload: {"type": "...", "param": "..."}

3. Run a security scan on it

4. If the scan passes, toggle it live

My endpoint accepts POST / with JSON body and has GET /health returning 200.`} />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, lineHeight: 1.6 }}>
                That's it. Claude will use the MCP tools to create your build, add tools, scan for security issues, and go live — all in one conversation.
                You can also say "list my builds" or "update my build's description" anytime.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 40 }}>
            <button style={s.btn} onClick={() => setShowForm(true)}>
              I'll do it manually — show the form
            </button>
            <button style={s.btnOutline} onClick={() => navigate('builder-docs')}>
              I prefer the REST API
            </button>
          </div>
        </>
      )}

      {/* Registration form */}
      {showForm && (
        <div style={s.form}>
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--accent2)', cursor: 'pointer' }} onClick={() => setShowForm(false)}>
              ← Back to guide
            </span>
          </div>

          <div style={s.section}>
            <div style={s.sectionTitle}>Build details</div>
            <div style={s.row}>
              <label style={s.label}>Build name *</label>
              <input style={s.input} value={form.name} onChange={e => { set('name', e.target.value); set('slug', autoSlug(e.target.value)); }} placeholder="My Analytics API" />
              <div style={s.hint}>A clear name that tells users what your build does.</div>
            </div>
            <div style={s.row}>
              <label style={s.label}>Slug</label>
              <input style={s.input} value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="my-analytics-api" />
              <div style={s.hint}>URL-safe identifier. Auto-generated from name. Must be unique.</div>
            </div>
            <div style={s.row}>
              <label style={s.label}>Description</label>
              <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What does your build do? What data or capabilities does it offer? Be specific — this is your sales pitch." />
            </div>
            <div style={s.row}>
              <label style={s.label}>Tags (comma-separated)</label>
              <input style={s.input} value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="analytics, crypto, real-time, market-data" />
              <div style={s.hint}>Help users find your build. Use relevant keywords.</div>
            </div>
            <div style={s.row}>
              <label style={s.label}>Example queries (one per line)</label>
              <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={form.exampleQueries} onChange={e => set('exampleQueries', e.target.value)} placeholder={"Get the last 30 days of daily BTC data\nRun a volatility analysis on ETH\nList all available datasets"} />
              <div style={s.hint}>Show users what they can ask your build. These appear on your marketplace listing.</div>
            </div>
          </div>

          <div style={s.section}>
            <div style={s.sectionTitle}>Endpoint & pricing</div>
            <div style={s.row}>
              <label style={s.label}>Endpoint URL *</label>
              <input style={s.input} value={form.endpointUrl} onChange={e => set('endpointUrl', e.target.value)} placeholder="https://your-tunnel-url.trycloudflare.com" />
              <div style={s.hint}>
                Your public HTTPS URL. Use <strong>Cloudflare Tunnel</strong> (free, permanent) or <strong>ngrok</strong> (free, temporary).
                Must have a /health route returning HTTP 200.
              </div>
            </div>
            <div style={s.row}>
              <label style={s.label}>Price per call (USD)</label>
              <input style={{ ...s.input, maxWidth: 140 }} type="number" min="0.01" step="0.01" value={form.pricePerCall} onChange={e => set('pricePerCall', e.target.value)} />
              <div style={s.hint}>You receive 70% of this. Tip: start low to attract early users.</div>
              <div style={{ marginTop: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pricing guide</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.8 }}>
                  <div><strong style={{ color: 'var(--text)' }}>$0.01 – $0.05</strong> — Simple lookups, cached data, basic queries</div>
                  <div><strong style={{ color: 'var(--text)' }}>$0.05 – $0.15</strong> — Database queries, light computation, scrapers</div>
                  <div><strong style={{ color: 'var(--text)' }}>$0.15 – $0.50</strong> — AI inference (small models), complex analysis</div>
                  <div><strong style={{ color: 'var(--text)' }}>$0.50 – $2.00</strong> — Large model inference (GPT-4, Claude, etc.)</div>
                  <div><strong style={{ color: 'var(--text)' }}>$2.00+</strong> — GPU-heavy tasks, image/video generation, long-running jobs</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>
                  Consider your actual compute costs. If your build wraps a paid API (OpenAI, Anthropic), price above your cost per call + margin.
                </div>
              </div>
            </div>
          </div>

          {error && <div style={s.error}>{error}</div>}

          <button style={s.btn} onClick={submit} disabled={loading}>
            {loading ? 'Publishing...' : 'Publish build →'}
          </button>
        </div>
      )}
    </div>
  );
}
